import { useMemo } from "react";

const MS_PER_DAY = 86400000;

const TODAY = "Today";
const YESTERDAY = "Yesterday";
const OLDER = "Older";

const EMPTY = Object.freeze({
  groups: [],
  groupCounts: [],
  items: [],
  firstUnreadGroupIndex: null,
});

/**
 * Groups notifications by Today / Yesterday / Older in a single O(N) pass
 * and emits the parallel-array shape consumed by react-virtuoso's
 * GroupedVirtuoso (https://virtuoso.dev/grouped-virtuoso/).
 *
 * `items` is the input array passed through by reference — no copy — so
 * GroupedVirtuoso indexes directly into the source.
 *
 * Assumes notifications are sorted by CreatedAt descending from the backend.
 *
 * @param {Array} notifications - notification objects with CreatedAt (ms) and IsRead
 * @returns {{
 *   groups: string[],
 *   groupCounts: number[],
 *   items: object[],
 *   firstUnreadGroupIndex: number | null,
 * }}
 */
const useGroupedNotifications = (notifications) =>
  useMemo(() => {
    if (!notifications?.length) {
      return EMPTY;
    }

    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();
    const yesterdayStart = todayStart - MS_PER_DAY;

    const groups = [];
    const groupCounts = [];
    let currentGroup = null;
    let firstUnreadGroupIndex = null;

    for (let i = 0; i < notifications.length; i += 1) {
      const { CreatedAt, IsRead } = notifications[i];

      let groupName;
      if (CreatedAt >= todayStart) {
        groupName = TODAY;
      } else if (CreatedAt >= yesterdayStart) {
        groupName = YESTERDAY;
      } else {
        groupName = OLDER;
      }

      if (currentGroup !== groupName) {
        groups.push(groupName);
        groupCounts.push(0);
        currentGroup = groupName;
      }

      groupCounts[groupCounts.length - 1] += 1;

      if (firstUnreadGroupIndex === null && !IsRead) {
        firstUnreadGroupIndex = groups.length - 1;
      }
    }

    return {
      groups,
      groupCounts,
      items: notifications,
      firstUnreadGroupIndex,
    };
  }, [notifications]);

export default useGroupedNotifications;
